import React from "react";
import {
  MousePointer2,
  Pencil,
  Square,
  Type,
  Undo2,
  Trash2,
  ZoomIn,
  ZoomOut,
  RotateCcw,
} from "lucide-react";
import type { TeacherCorrectionTool } from "./teacherCorrectionTypes";

export interface TeacherCorrectionToolbarProps {
  activeTool: TeacherCorrectionTool;
  onToolChange: (tool: TeacherCorrectionTool) => void;
  onUndo: () => void;
  onClear: () => void;
  canUndo: boolean;
  hasMarks: boolean;
  disabled?: boolean;
  /** View zoom factor (1 = 100%), controlled by parent editor. */
  userZoom: number;
  onZoomIn: () => void;
  onZoomOut: () => void;
  onResetZoom: () => void;
  zoomAtMin: boolean;
  zoomAtMax: boolean;
  /** True when zoom is ~100% (reset control disabled). */
  zoomIsDefault: boolean;
}

export const TeacherCorrectionToolbar: React.FC<TeacherCorrectionToolbarProps> = ({
  activeTool,
  onToolChange,
  onUndo,
  onClear,
  canUndo,
  hasMarks,
  disabled,
  userZoom,
  onZoomIn,
  onZoomOut,
  onResetZoom,
  zoomAtMin,
  zoomAtMax,
  zoomIsDefault,
}) => {
  const btn = (active: boolean) =>
    `inline-flex items-center justify-center gap-1.5 rounded-lg border px-2.5 py-1.5 text-xs font-medium transition-colors ${
      active
        ? "border-primary-500 bg-primary-50 text-primary-800 dark:bg-primary-900/40 dark:text-primary-200 dark:border-primary-400"
        : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700"
    } ${disabled ? "opacity-50 pointer-events-none" : ""}`;

  return (
    <div className="flex flex-wrap items-center gap-2 mb-2">
      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-1">Mark up:</span>
      <button
        type="button"
        className={btn(activeTool === "select")}
        onClick={() => onToolChange("select")}
        disabled={disabled}
        aria-pressed={activeTool === "select"}
      >
        <MousePointer2 className="w-3.5 h-3.5 shrink-0" />
        Select
      </button>
      <button
        type="button"
        className={btn(activeTool === "pen")}
        onClick={() => onToolChange("pen")}
        disabled={disabled}
        aria-pressed={activeTool === "pen"}
      >
        <Pencil className="w-3.5 h-3.5 shrink-0" />
        Pen
      </button>
      <button
        type="button"
        className={btn(activeTool === "rectangle")}
        onClick={() => onToolChange("rectangle")}
        disabled={disabled}
        aria-pressed={activeTool === "rectangle"}
      >
        <Square className="w-3.5 h-3.5 shrink-0" />
        Rectangle
      </button>
      <button
        type="button"
        className={btn(activeTool === "text")}
        onClick={() => onToolChange("text")}
        disabled={disabled}
        aria-pressed={activeTool === "text"}
      >
        <Type className="w-3.5 h-3.5 shrink-0" />
        Text
      </button>
      <button
        type="button"
        className={btn(false)}
        onClick={onUndo}
        disabled={disabled || !canUndo}
        title="Undo last mark"
      >
        <Undo2 className="w-3.5 h-3.5 shrink-0" />
        Undo
      </button>
      <button
        type="button"
        className={btn(false)}
        onClick={onClear}
        disabled={disabled || !hasMarks}
        title="Clear all marks"
      >
        <Trash2 className="w-3.5 h-3.5 shrink-0" />
        Clear
      </button>

      <span className="hidden sm:inline-block w-px h-6 bg-slate-200 dark:bg-slate-600 mx-0.5 self-center" aria-hidden />

      <span className="text-xs font-medium text-slate-500 dark:text-slate-400 mr-0.5">Zoom:</span>
      <button
        type="button"
        className={btn(false)}
        onClick={onZoomOut}
        disabled={disabled || zoomAtMin}
        title="Zoom out"
        aria-label="Zoom out"
      >
        <ZoomOut className="w-3.5 h-3.5 shrink-0" />
      </button>
      <span
        className="min-w-[2.75rem] text-center text-xs font-medium tabular-nums text-slate-600 dark:text-slate-300 px-1"
        title="Current zoom"
      >
        {Math.round(userZoom * 100)}%
      </span>
      <button
        type="button"
        className={btn(false)}
        onClick={onZoomIn}
        disabled={disabled || zoomAtMax}
        title="Zoom in"
        aria-label="Zoom in"
      >
        <ZoomIn className="w-3.5 h-3.5 shrink-0" />
      </button>
      <button
        type="button"
        className={btn(false)}
        onClick={onResetZoom}
        disabled={disabled || zoomIsDefault}
        title="Reset zoom to 100%"
        aria-label="Reset zoom"
      >
        <RotateCcw className="w-3.5 h-3.5 shrink-0" />
      </button>
    </div>
  );
};
